<script setup lang="ts">
import { useTranslation } from "i18next-vue";
import { animate, motion } from "motion-v";
import { computed, onMounted, onUnmounted, ref, useTemplateRef, watch } from "vue";
import IconBarcodeScanner from "#/assets/iconsDynamic/BarcodeScanner.vue";
import IconLocked from "#/assets/iconsDynamic/Locked.vue";
import IconMark from "#/assets/iconsDynamic/Mark.vue";
import IconPincode from "#/assets/iconsDynamic/Pincode.vue";
import IconWarn from "#/assets/iconsDynamic/Warn.vue";
import { useHidScanner } from "#/composables/useHidScanner";
import { useModal } from "#/composables/useModal";
import { useMainStore } from "#/store/main";
import { useModalStore } from "#/store/modals";
import { useThemeStore } from "#/store/theme";
import type { IProduct } from "#/types/iCardProduct";
import { getLocaleName } from "#/utils/i18next";

const emit = defineEmits<(e: "confirm") => void>();

const { t, i18next } = useTranslation();
const { visible, close } = useModal<IProduct>("cart-adult-confirm");

const modals = useModalStore();
const storeMain = useMainStore();
const themeStore = useThemeStore();

const SUCCESS_TIMEOUT = 1000;
let successTimeout: ReturnType<typeof setTimeout> | null = null;

const adultModalWrapper = useTemplateRef("adultModalWrapper");
const mode = ref<"barcode" | "pin">("barcode");
const pin = computed(() => themeStore.data?.kiosk_age_confirmation_pin || "");
const barcode = computed(() => themeStore.data?.kiosk_age_confirmation_barcode || "");
const pinInput = ref("");
const isSegmentsVisible = computed(() => pin?.value?.length && barcode.value?.length);
const isPinError = ref(false);
const isBarcodeScanning = ref(false);
const isBarcodeError = ref(false);
const isSuccess = ref(false);

const pinDots = computed(() => Array.from({ length: pin.value?.length ?? 4 }, (_, index) => (pinInput.value?.[index] ? 1 : 0)));
const adultProducts = computed(() => storeMain.cartProducts_lib?.filter((product) => product.adult) || []);
const badges = computed(() => Array.from(new Set(adultProducts.value.map((product) => getLocaleName(product, i18next.language)))));

const shake = () => animate(adultModalWrapper.value, { x: [0, -10, 0, 10, 0] }, { duration: 0.15, repeat: 1 });

const changeMode = (value: "barcode" | "pin") => {
	reset();
	mode.value = value;
};

const checkPin = () => {
	if (pinInput.value.length !== pin.value?.length) return;
	if (pinInput.value === pin.value) return onSuccess();

	isPinError.value = true;
	pinInput.value = "";
	shake();
};

const onPinKey = (key: string) => {
	isPinError.value = false;
	if (pinInput.value.length < pin.value?.length) pinInput.value += key;
	checkPin();
};

const onPinBackspace = () => {
	pinInput.value = pinInput.value.slice(0, -1);
	checkPin();
};

const onScanBarcode = (code: string) => {
	isBarcodeScanning.value = true;
	if (code === barcode.value || code === pin.value) {
		onSuccess();
	} else {
		isBarcodeError.value = true;
		shake();
	}
	isBarcodeScanning.value = false;
};

const _onKeyDown = (e: KeyboardEvent) => {
	if (!visible.value) return;
	onKeyDown(e);
};

const reset = () => {
	isSuccess.value = false;
	isPinError.value = false;
	pinInput.value = "";
	mode.value = barcode.value?.length ? "barcode" : "pin";
	isBarcodeError.value = false;
	isBarcodeScanning.value = false;
};

const onConfirm = () => {
	reset();
	emit("confirm");
	close();
};

const onSuccess = () => {
	isSuccess.value = true;
	successTimeout = setTimeout(onConfirm, SUCCESS_TIMEOUT);
};

const onCloseClick = () => {
	if (!isSuccess.value) return;
	if (successTimeout) clearTimeout(successTimeout);
	onConfirm();
};

const onClose = () => {
	reset();
	close();
	modals.open("cart-adult-reset");
};

const { onKeyDown } = useHidScanner({ onScan: onScanBarcode, minLength: 3, maxKeyInterval: 90, registerListener: false });
onMounted(() => window.addEventListener("keydown", _onKeyDown));
onUnmounted(() => {
	if (successTimeout) clearTimeout(successTimeout);
	window.removeEventListener("keydown", _onKeyDown);
});

watch(barcode, (value) => !value?.length && changeMode("pin"), { immediate: true });
</script>

<template>
	<Transition name="fade">
		<div v-if="visible" class="cart_adult_modal_overlay" @click.self="onCloseClick">
			<div ref="adultModalWrapper" class="cart_adult_modal_content">
				<Transition name="fade" mode="out-in">
					<div v-if="isSuccess" class="successwrap">
						<div class="okcircle"><IconMark /></div>
						<h2 class="mtitle">{{ t("modal.cart-adult-confirm.success.title") }}</h2>
						<p class="msub">{{ t("modal.cart-adult-confirm.success.subtitle") }}</p>
					</div>

					<div v-else>
						<button type="button" class="cart_adult_close" @click="onClose">✕</button>
						<div class="agehead">
							<div class="agehead_icon">18+</div>
							<h2 class="mtitle">{{ t("modal.cart-adult-confirm.title") }}</h2>
							<p v-if="themeStore.data?.kiosk_age_confirmation_warning_text?.length" class="lawnote">
								{{ themeStore.data?.kiosk_age_confirmation_warning_text }}
							</p>
						</div>
						<div class="itemchips">
							<span class="ichip" v-for="product in badges" :key="product">
								<IconLocked /> {{ product }}
							</span>
						</div>

						<div class="seg" v-if="isSegmentsVisible">
							<button type="button" :class="{ on: mode === 'barcode' }" @click="changeMode('barcode')">
								<span> <IconBarcodeScanner /> {{ t("modal.cart-adult-confirm.barcode.button.scan") }} </span>
								<motion.div v-if="mode === 'barcode'" class="seg-adult-underline" layout-id="seg-adult-underline" />
							</button>
							<button type="button" :class="{ on: mode === 'pin' }" @click="changeMode('pin')">
								<span> <IconPincode /> {{ t("modal.cart-adult-confirm.pin.button.pin") }} </span>
								<motion.div v-if="mode === 'pin'" class="seg-adult-underline" layout-id="seg-adult-underline" />
							</button>
						</div>

						<Transition name="fade" mode="out-in">
							<div v-if="mode === 'barcode'" class="scanwrap" :class="{ scanning: isBarcodeScanning, error: isBarcodeError }">
								<div class="badge3d">
									<div class="hole"></div>
									<div class="ph"></div>
									<div class="l1"></div>
									<div class="l2"></div>
									<div class="strip"></div>
									<div class="scanline"></div>
								</div>
								<div class="scanhint">
									{{ isBarcodeError ? t("modal.cart-adult-confirm.barcode.title.error") : t("modal.cart-adult-confirm.barcode.title") }}
								</div>
								<div class="scansub">
									{{ isBarcodeError ? t("modal.cart-adult-confirm.barcode.subtitle.error") : t("modal.cart-adult-confirm.barcode.subtitle") }}
								</div>
								<div class="scanerr" v-if="isBarcodeError">
									<IconWarn />
									<div>
										<div class="et">{{ t("modal.cart-adult-confirm.barcode.error.not-recognized") }}</div>
										<div class="ed">{{ t("modal.cart-adult-confirm.barcode.error.subtitle") }}</div>
									</div>
								</div>
							</div>

							<div v-else-if="mode === 'pin'" class="pinpad" :class="{ error: isPinError }">
								<div class="pindots"><i v-for="(dot, index) in pinDots" :key="index" :class="{ fill: dot === 1 }"></i></div>
								<div class="pinerr">{{ t("modal.cart-adult-confirm.pin.error.incorrect") }}</div>
								<div class="keypad">
									<button type="button" @click="onPinKey('1')">1</button>
									<button type="button" @click="onPinKey('2')">2</button>
									<button type="button" @click="onPinKey('3')">3</button>
									<button type="button" @click="onPinKey('4')">4</button>
									<button type="button" @click="onPinKey('5')">5</button>
									<button type="button" @click="onPinKey('6')">6</button>
									<button type="button" @click="onPinKey('7')">7</button>
									<button type="button" @click="onPinKey('8')">8</button>
									<button type="button" @click="onPinKey('9')">9</button>
									<button type="button" class="ghost" @click="onPinBackspace()">⌫</button>
									<button type="button" @click="onPinKey('0')">0</button>
									<button type="button" class="ghost" style="visibility: hidden;"></button>
								</div>
							</div>
						</Transition>
					</div>
				</Transition>
			</div>
		</div>
	</Transition>
</template>

<style lang="scss" scoped>
  @use './cartAdultConfirm.scss';
</style>
